import type { Category, CustomFieldDefinition, CustomFieldValue, Todo } from "../../domain/entities.ts";
import type { Root } from "../../domain/app-types.ts";
import { validTimelineDate } from "../tasks/collection-timeline.ts";

export function customFieldsForRoot(roots: Root[], id: string): CustomFieldDefinition[] {
  return roots.find(root => root.id === id)?.task_defaults?.custom_fields || [];
}
export function availableCustomFields(roots: Root[], categories: Category[], tasks: Todo[], activeRoot: string | null, collectionId: string | null): CustomFieldDefinition[] {
  const rootIds = new Set(tasks.map(task => task.rootId)); if (!rootIds.size && activeRoot) rootIds.add(activeRoot);
  const schema = new Map<string, CustomFieldDefinition>();
  for (const task of tasks) for (const field of Object.values(task.customFieldSchema || {})) schema.set(field.id, field);
  for (const category of categories.filter(category => rootIds.has(category.rootId))) for (const field of Object.values(category.customFieldSchema || {})) schema.set(field.id, field);
  for (const root of roots.filter(root => rootIds.has(root.id))) for (const field of root.task_defaults?.custom_fields || []) schema.set(field.id, field);
  if (!collectionId) return [...schema.values()];
  const enabled = new Set(categories.find(category => category.id === collectionId)?.customFieldIds || []);
  for (const task of tasks.filter(task => task.categoryId === collectionId)) for (const id of Object.keys(task.customFields || {})) enabled.add(id);
  return [...schema.values()].filter(field => enabled.has(field.id));
}
export function customFieldsForTask(task: Todo, roots: Root[], categories: Category[]): CustomFieldDefinition[] {
  const category = categories.find(item => item.id === task.categoryId);
  const fields = new Map<string, CustomFieldDefinition>([
    ...Object.entries(task.customFieldSchema || {}), ...Object.entries(category?.customFieldSchema || {}),
    ...customFieldsForRoot(roots, task.rootId).map(field => [field.id, field] as const),
  ]);
  const ids = [...new Set([...(category?.customFieldIds || []), ...Object.keys(task.customFields || {})])];
  return ids.map(id => fields.get(id)).filter((field): field is CustomFieldDefinition => !!field);
}
export function customFieldValue(field: CustomFieldDefinition, raw: unknown): CustomFieldValue {
  if (raw === "" || raw == null) return null;
  if (field.kind === "number") {
    const value = Number(raw);
    if (!Number.isFinite(value) || Math.abs(value) > 1e15) throw new Error(`${field.name}: enter a finite number up to 10¹⁵.`);
    return value;
  }
  const value = String(raw).trim();
  if (field.kind === "date" && !validTimelineDate(value)) throw new Error(`${field.name}: choose a valid date.`);
  if (field.kind === "choice" && !field.choices.includes(value)) throw new Error(`${field.name}: choose an available option.`);
  if (field.kind === "url") {
    let url: URL; try { url = new URL(value); } catch { throw new Error(`${field.name}: enter a full HTTP or HTTPS URL.`); }
    if (!["http:", "https:"].includes(url.protocol) || value.length > 2048) throw new Error(`${field.name}: enter a full HTTP or HTTPS URL.`);
  }
  return value;
}

export function applyCustomFieldForm(task: Todo, form: HTMLFormElement): void {
  for (const control of form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-custom-field]")) {
    const field = JSON.parse(control.dataset.customField!) as CustomFieldDefinition;
    const value = customFieldValue(field, control.value);
    task.customFields ||= {}; task.customFieldSchema ||= {};
    task.customFields[field.id] = value; task.customFieldSchema[field.id] = structuredClone(field);
  }
}
